import React, { useMemo } from 'react';
import { Alert, Tag, Typography } from 'antd';
import type { ExecutionProgressHydration } from '../executionOperations/types';
import type { HumanizedCargoMeta } from '../routes/execution/cargoHumanization';
import type { ExecutionIncident } from '../routes/execution/incidentFacade';
import type { OperationalAttention } from '../operationalDomain/processes/operationalProcessTypes';
import { buildOperationalCockpit } from './buildOperationalCockpit';
import type { OperationalCockpitModel } from './types';
import { formatQtyPlaces } from './operationalCockpitLabels';
import { RouteHealthStrip } from '../operationalIntelligence';
import { OperationalImpactStrip } from '../operationalCoordination';
import { RecoveryStrip } from '../operationalRecovery';
import './operationalCockpit.css';

const { Text } = Typography;

export type OperationalRouteCockpitProps = {
  hydration: ExecutionProgressHydration;
  cargoIndex: Map<number, HumanizedCargoMeta>;
  incidents?: ExecutionIncident[];
  processAttentions?: OperationalAttention[];
  activeStopUid?: string | null;
  actionsSlot?: React.ReactNode;
  /** Pre-built model avoids duplicate hydration work in parent workspace. */
  model?: OperationalCockpitModel | null;
};

export const OperationalRouteCockpit: React.FC<OperationalRouteCockpitProps> = ({
  hydration,
  cargoIndex,
  incidents = [],
  processAttentions = [],
  activeStopUid = null,
  actionsSlot,
  model: modelProp
}) => {
  const model = useMemo(
    () =>
      modelProp ??
      buildOperationalCockpit({
        hydration,
        cargoIndex,
        incidents,
        processAttentions,
        activeStopUid
      }),
    [modelProp, hydration, cargoIndex, incidents, processAttentions, activeStopUid]
  );

  if (!model) {
    return (
      <div className="op-cockpit op-cockpit--degraded">
        {actionsSlot ? <div className="op-cockpit__hero-actions">{actionsSlot}</div> : null}
        <Alert
          type="info"
          showIcon
          message="Операционная панель"
          description="Данные исполнения загружаются. Убедитесь, что включён режим projection на API."
        />
      </div>
    );
  }

  const {
    narrative,
    vehicleLoad,
    cargoFlow,
    timeline,
    currentOperation,
    executionAttentions,
    driverPlaceholders,
    intelligence,
    coordination,
    recovery
  } = model;
  const inVehicle = cargoFlow.filter((c) => c.inVehicle);

  return (
    <div className="op-cockpit">
      {intelligence ? <RouteHealthStrip intelligence={intelligence} /> : null}
      {coordination ? <OperationalImpactStrip coordination={coordination} /> : null}
      {recovery ? <RecoveryStrip recovery={recovery} /> : null}
      {currentOperation || actionsSlot ? (
        <section className="op-cockpit__hero">
          {currentOperation ? (
            <>
              <div className="op-cockpit__hero-title">{currentOperation.title}</div>
              <div className="op-cockpit__hero-grid">
                {currentOperation.applicationNumber ? (
                  <div>
                    <Text type="secondary">Заявка</Text>
                    <div>№{currentOperation.applicationNumber}</div>
                  </div>
                ) : null}
                {currentOperation.cargoName ? (
                  <div>
                    <Text type="secondary">Груз</Text>
                    <div>{currentOperation.cargoName}</div>
                  </div>
                ) : null}
                {currentOperation.address ? (
                  <div>
                    <Text type="secondary">Адрес</Text>
                    <div>{currentOperation.address}</div>
                  </div>
                ) : null}
                {currentOperation.unloadedQty != null ? (
                  <div>
                    <Text type="secondary">Выгружено</Text>
                    <div>{formatQtyPlaces(currentOperation.unloadedQty)}</div>
                  </div>
                ) : null}
                {currentOperation.remainingQty != null && currentOperation.remainingQty > 0 ? (
                  <div>
                    <Text type="secondary">Осталось</Text>
                    <div>{formatQtyPlaces(currentOperation.remainingQty)}</div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
          {actionsSlot ? <div className="op-cockpit__hero-actions">{actionsSlot}</div> : null}
        </section>
      ) : null}

      {executionAttentions.length > 0 ? (
        <section className="op-cockpit__attentions">
          {executionAttentions.map((a) => (
            <Tag
              key={a.id}
              color={a.severity === 'critical' ? 'error' : a.severity === 'warning' ? 'warning' : 'default'}
            >
              {a.title}
            </Tag>
          ))}
        </section>
      ) : null}

      <section className="op-cockpit__vehicle">
        <div className="op-cockpit__section-title">Состояние машины</div>
        <div className="op-cockpit__vehicle-row">
          <span>В машине: <strong>{formatQtyPlaces(vehicleLoad.inVehiclePlaces)}</strong></span>
          <span>Доставлено: <strong>{formatQtyPlaces(vehicleLoad.deliveredPlaces)}</strong></span>
          <span>Осталось везти: <strong>{formatQtyPlaces(vehicleLoad.remainingPlaces)}</strong></span>
          {vehicleLoad.weightKg != null ? <span>Вес: <strong>{vehicleLoad.weightKg} кг</strong></span> : null}
          {vehicleLoad.volumeM3 != null ? <span>Объём: <strong>{vehicleLoad.volumeM3} м³</strong></span> : null}
          {vehicleLoad.freePlacesEstimate != null ? (
            <span>Свободно: <strong>{formatQtyPlaces(vehicleLoad.freePlacesEstimate)}</strong></span>
          ) : null}
        </div>
      </section>

      <section className="op-cockpit__narrative">
        <div className="op-cockpit__section-title">Маршрут</div>
        {narrative.map((seg) => (
          <div key={seg.key} className="op-cockpit__narrative-segment">
            <div className="op-cockpit__narrative-city">{seg.city}</div>
            <div className="op-cockpit__narrative-status">{seg.statusLine}</div>
            {seg.detailLines.map((line) => (
              <div key={line} className="op-cockpit__narrative-detail">
                {line}
              </div>
            ))}
            {seg.connectorAfter ? (
              <div className="op-cockpit__narrative-connector">{seg.connectorAfter}</div>
            ) : null}
          </div>
        ))}
      </section>

      <section className="op-cockpit__cargo">
        <div className="op-cockpit__section-title">Грузы в работе ({inVehicle.length})</div>
        <ul className="op-cockpit__cargo-list">
          {cargoFlow.map((row) => (
            <li key={`${row.cargoId}-${row.fromLabel}-${row.toLabel}`} className="op-cockpit__cargo-item">
              <div className="op-cockpit__cargo-name">
                {row.cargoName}
                {row.applicationNumber ? ` · №${row.applicationNumber}` : ''}
              </div>
              <div className="op-cockpit__cargo-meta">
                {row.shipper ? `${row.shipper}` : ''}
                {row.fromLabel && row.toLabel ? ` · ${row.fromLabel} → ${row.toLabel}` : ''}
              </div>
              <div className="op-cockpit__cargo-state">
                <Tag color={row.inVehicle ? 'processing' : row.stateLabel.includes('Достав') ? 'success' : 'default'}>
                  {row.stateLabel}
                </Tag>
                {row.inVehicle ? ` · в машине ${formatQtyPlaces(row.remainingQty)}` : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {timeline.length > 0 ? (
        <section className="op-cockpit__timeline">
          <div className="op-cockpit__section-title">Хронология</div>
          <ul className="op-cockpit__timeline-list">
            {timeline.map((t) => (
              <li
                key={t.key}
                className={
                  t.completed
                    ? 'op-cockpit__timeline-done'
                    : t.kind === 'recovery'
                      ? 'op-cockpit__timeline-recovery'
                      : ''
                }
              >
                {t.label}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="op-cockpit__driver">
        <div className="op-cockpit__section-title">Контекст водителя (скоро)</div>
        <div className="op-cockpit__driver-row">
          {driverPlaceholders.map((p) => (
            <span key={p.key} className="op-cockpit__driver-chip">
              {p.label}: {p.value ?? '—'}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
};

export default OperationalRouteCockpit;
